import React, { useEffect, useState } from 'react';
import { UserX, Edit, Check, X, Wallet, Ban, CheckCircle, Search, RefreshCw, Eye, Key, LogIn, User, ShoppingBag, ArrowRightLeft, Users } from 'lucide-react';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../context/AuthContext';

const AdminUsers = () => {
  const { login } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [walletModal, setWalletModal] = useState(null);
  const [walletAction, setWalletAction] = useState({ type: 'cash_wallet', amount: 0, action: 'add' });
  const [passwordModal, setPasswordModal] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [viewUserModal, setViewUserModal] = useState(null);
  const [viewUserData, setViewUserData] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await api.get('/admin/users');
      // Handle both array and object with users property
      const userData = Array.isArray(response.data) ? response.data : response.data.users || [];
      setUsers(userData);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleBlockToggle = async (userId, currentStatus) => {
    try {
      await api.post(`/admin/users/${userId}/toggle-block`);
      toast.success(currentStatus ? 'User unblocked' : 'User blocked');
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    try {
      await api.put(`/admin/users/${editingUser.id}`, {
        name: editingUser.name,
        email: editingUser.email,
        whatsapp_number: editingUser.whatsapp_number,
        username: editingUser.username
      });
      toast.success('User updated successfully');
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    }
  };

  const handleWalletUpdate = async (e) => {
    e.preventDefault();
    try {
      await api.post(`/admin/users/${walletModal.id}/wallet`, {
        wallet_type: walletAction.type,
        amount: parseFloat(walletAction.amount),
        action: walletAction.action
      });
      toast.success(`Wallet ${walletAction.action === 'add' ? 'credited' : 'debited'} successfully`);
      setWalletModal(null);
      setWalletAction({ type: 'cash_wallet', amount: 0, action: 'add' });
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update wallet');
    }
  };

  // Change user password
  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters');
      return;
    }
    try {
      await api.post(`/admin/users/${passwordModal.id}/change-password`, {
        new_password: newPassword
      });
      toast.success('Password changed successfully');
      setPasswordModal(null);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    }
  };

  // Login as user
  const handleLoginAsUser = async (user) => {
    try {
      const res = await api.post(`/admin/users/${user.id}/login-as`);
      // Store admin token to return later
      const adminToken = localStorage.getItem('token');
      localStorage.setItem('admin_return_token', adminToken);
      
      // Login as the target user
      localStorage.setItem('token', res.data.token);
      toast.success(`Logged in as ${user.name} (@${user.username})`);
      
      // Redirect to user dashboard
      window.location.href = '/dashboard';
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to login as user');
    }
  };

  // View user details
  const handleViewUser = async (user) => {
    setViewUserModal(user);
    setLoadingDetails(true);
    try {
      const res = await api.get(`/admin/users/${user.id}/full-details`);
      setViewUserData(res.data);
    } catch (error) {
      toast.error('Failed to load user details');
      setViewUserModal(null);
    } finally {
      setLoadingDetails(false);
    }
  };

  const filteredUsers = users.filter(user => 
    user.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.username?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    user.whatsapp_number?.includes(searchTerm)
  );

  if (loading) {
    return (
      <div className="min-h-screen pt-24 pb-16 px-6 flex items-center justify-center">
        <div className="text-2xl">Loading users...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-16 px-6" data-testid="admin-users-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-8">
          <h1 className="text-4xl md:text-5xl font-bold" data-testid="users-title">Manage Members</h1>
          
          <div className="flex gap-3">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search members..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-4 py-2 bg-white/5 border border-white/10 rounded-lg w-64"
              />
            </div>
            <button onClick={fetchUsers} className="p-2 bg-white/5 rounded-lg hover:bg-white/10">
              <RefreshCw className="w-5 h-5" />
            </button>
          </div>
        </div>

        {filteredUsers.length > 0 ? (
          <div className="glass-card p-6">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-white/10">
                    <th className="text-left py-4 px-4">Member</th>
                    <th className="text-left py-4 px-4">Contact</th>
                    <th className="text-left py-4 px-4">Wallets</th>
                    <th className="text-left py-4 px-4">Status</th>
                    <th className="text-left py-4 px-4">Joined</th>
                    <th className="text-left py-4 px-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => (
                    <tr key={user.id} className="border-b border-white/5 hover:bg-white/5" data-testid={`user-row-${user.id}`}>
                      <td className="py-4 px-4">
                        <p className="font-semibold">{user.name}</p>
                        <p className="text-sm text-muted-foreground">@{user.username}</p>
                      </td>
                      <td className="py-4 px-4">
                        <p className="text-sm">{user.email}</p>
                        <p className="text-sm text-muted-foreground">{user.whatsapp_number || 'N/A'}</p>
                      </td>
                      <td className="py-4 px-4">
                        <div className="text-xs space-y-1">
                          <p>Cash: <span className="text-primary font-semibold">₹{(user.cash_wallet || 0).toFixed(2)}</span></p>
                          <p>Commission: <span className="text-accent font-semibold">₹{(user.commission_wallet || 0).toFixed(2)}</span></p>
                          <p>Promo: <span className="text-green-500 font-semibold">₹{(user.promotion_wallet || 0).toFixed(2)}</span></p>
                        </div>
                      </td>
                      <td className="py-4 px-4">
                        <div className="space-y-1">
                          <span className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            user.role === 'admin' 
                              ? 'bg-primary/20 text-primary' 
                              : 'bg-secondary/20 text-secondary'
                          }`}>
                            {user.role}
                          </span>
                          {user.is_activated ? (
                            <span className="block px-2 py-1 bg-green-500/20 text-green-500 rounded-full text-xs">Active</span>
                          ) : (
                            <span className="block px-2 py-1 bg-yellow-500/20 text-yellow-500 rounded-full text-xs">Free</span>
                          )}
                          {user.is_blocked && (
                            <span className="block px-2 py-1 bg-red-500/20 text-red-500 rounded-full text-xs">Blocked</span>
                          )}
                        </div>
                      </td>
                      <td className="py-4 px-4 text-muted-foreground text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-4">
                        <div className="flex gap-2 flex-wrap">
                          <button
                            onClick={() => handleViewUser(user)}
                            className="p-2 bg-blue-500/20 text-blue-500 rounded-lg hover:bg-blue-500/30"
                            title="View Full Details"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setEditingUser(user)}
                            className="p-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30"
                            title="Edit User"
                          >
                            <Edit className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setWalletModal(user)}
                            className="p-2 bg-accent/20 text-accent rounded-lg hover:bg-accent/30"
                            title="Manage Wallet"
                          >
                            <Wallet className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setPasswordModal(user)}
                            className="p-2 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30"
                            title="Change Password"
                          >
                            <Key className="w-4 h-4" />
                          </button>
                          {user.role !== 'admin' && (
                            <>
                              <button
                                onClick={() => handleLoginAsUser(user)}
                                className="p-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30"
                                title="Login as User"
                              >
                                <LogIn className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleBlockToggle(user.id, user.is_blocked)}
                                className={`p-2 rounded-lg ${
                                  user.is_blocked 
                                    ? 'bg-green-500/20 text-green-500 hover:bg-green-500/30' 
                                    : 'bg-red-500/20 text-red-500 hover:bg-red-500/30'
                                }`}
                                title={user.is_blocked ? 'Unblock User' : 'Block User'}
                              >
                                {user.is_blocked ? <CheckCircle className="w-4 h-4" /> : <Ban className="w-4 h-4" />}
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ) : (
          <div className="text-center py-16 glass-card" data-testid="no-users">
            <h2 className="text-2xl font-bold mb-4">No users found</h2>
            <p className="text-muted-foreground">Try adjusting your search</p>
          </div>
        )}

        {/* Edit User Modal */}
        {editingUser && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-6">Edit Member</h2>
              <form onSubmit={handleUpdateUser} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={editingUser.name}
                    onChange={(e) => setEditingUser({ ...editingUser, name: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Username</label>
                  <input
                    type="text"
                    value={editingUser.username}
                    onChange={(e) => setEditingUser({ ...editingUser, username: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={editingUser.email}
                    onChange={(e) => setEditingUser({ ...editingUser, email: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">WhatsApp Number</label>
                  <input
                    type="text"
                    value={editingUser.whatsapp_number || ''}
                    onChange={(e) => setEditingUser({ ...editingUser, whatsapp_number: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">Save Changes</button>
                  <button
                    type="button"
                    onClick={() => setEditingUser(null)}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Wallet Management Modal */}
        {walletModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-2">Manage Wallet</h2>
              <p className="text-muted-foreground mb-6">{walletModal.name} (@{walletModal.username})</p>
              
              {/* Current Balances */}
              <div className="grid grid-cols-3 gap-3 mb-6">
                <div className="bg-white/5 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Cash</p>
                  <p className="text-lg font-bold text-primary">₹{(walletModal.cash_wallet || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white/5 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Commission</p>
                  <p className="text-lg font-bold text-accent">₹{(walletModal.commission_wallet || 0).toFixed(2)}</p>
                </div>
                <div className="bg-white/5 p-3 rounded-lg text-center">
                  <p className="text-xs text-muted-foreground">Promo</p>
                  <p className="text-lg font-bold text-green-500">₹{(walletModal.promotion_wallet || 0).toFixed(2)}</p>
                </div>
              </div>
              
              <form onSubmit={handleWalletUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">Wallet Type</label>
                  <select
                    value={walletAction.type}
                    onChange={(e) => setWalletAction({ ...walletAction, type: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                  >
                    <option value="cash_wallet">Cash Wallet</option>
                    <option value="commission_wallet">Commission Wallet</option>
                    <option value="promotion_wallet">Promotion Wallet</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Action</label>
                  <div className="flex gap-3">
                    <button
                      type="button"
                      onClick={() => setWalletAction({ ...walletAction, action: 'add' })}
                      className={`flex-1 py-3 rounded-xl font-semibold ${
                        walletAction.action === 'add' ? 'bg-green-500 text-white' : 'bg-white/5'
                      }`}
                    >
                      Add Funds
                    </button>
                    <button
                      type="button"
                      onClick={() => setWalletAction({ ...walletAction, action: 'deduct' })}
                      className={`flex-1 py-3 rounded-xl font-semibold ${
                        walletAction.action === 'deduct' ? 'bg-red-500 text-white' : 'bg-white/5'
                      }`}
                    >
                      Deduct Funds
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Amount (₹)</label>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    value={walletAction.amount}
                    onChange={(e) => setWalletAction({ ...walletAction, amount: e.target.value })}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    {walletAction.action === 'add' ? 'Add' : 'Deduct'} ₹{walletAction.amount || 0}
                  </button>
                  <button
                    type="button"
                    onClick={() => { setWalletModal(null); setWalletAction({ type: 'cash_wallet', amount: 0, action: 'add' }); }}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Password Change Modal */}
        {passwordModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6">
            <div className="glass-card p-8 max-w-md w-full">
              <h2 className="text-2xl font-bold mb-2">Change Password</h2>
              <p className="text-muted-foreground mb-6">{passwordModal.name} (@{passwordModal.username})</p>
              
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">New Password</label>
                  <input
                    type="password"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl"
                    placeholder="Enter new password (min 6 chars)"
                    minLength={6}
                    required
                  />
                </div>
                <div className="flex gap-3 pt-4">
                  <button type="submit" className="flex-1 btn-primary">
                    <Key className="w-4 h-4 inline mr-2" />
                    Change Password
                  </button>
                  <button
                    type="button"
                    onClick={() => { setPasswordModal(null); setNewPassword(''); }}
                    className="flex-1 px-4 py-3 bg-white/5 rounded-full font-semibold"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* View User Details Modal */}
        {viewUserModal && (
          <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-6 overflow-y-auto">
            <div className="glass-card p-8 max-w-4xl w-full my-8">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h2 className="text-2xl font-bold">{viewUserModal.name}</h2>
                  <p className="text-muted-foreground">@{viewUserModal.username} • {viewUserModal.email}</p>
                </div>
                <button
                  onClick={() => { setViewUserModal(null); setViewUserData(null); }}
                  className="p-2 hover:bg-white/10 rounded-lg"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {loadingDetails ? (
                <div className="text-center py-8">Loading details...</div>
              ) : viewUserData ? (
                <div className="space-y-6">
                  {/* User Info */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="bg-white/5 p-4 rounded-xl">
                      <p className="text-xs text-muted-foreground">Cash Wallet</p>
                      <p className="text-xl font-bold text-primary">₹{(viewUserData.user?.cash_wallet || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-xl">
                      <p className="text-xs text-muted-foreground">Commission</p>
                      <p className="text-xl font-bold text-accent">₹{(viewUserData.user?.commission_wallet || 0).toFixed(2)}</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-xl">
                      <p className="text-xs text-muted-foreground">Direct Referrals</p>
                      <p className="text-xl font-bold text-blue-500">{viewUserData.mlm_stats?.direct_referrals || 0}</p>
                    </div>
                    <div className="bg-white/5 p-4 rounded-xl">
                      <p className="text-xs text-muted-foreground">Active Referrals</p>
                      <p className="text-xl font-bold text-green-500">{viewUserData.mlm_stats?.active_referrals || 0}</p>
                    </div>
                  </div>

                  {/* Quick Actions */}
                  <div className="flex gap-3 flex-wrap">
                    <button
                      onClick={() => { setViewUserModal(null); setEditingUser(viewUserModal); }}
                      className="px-4 py-2 bg-primary/20 text-primary rounded-lg hover:bg-primary/30 flex items-center gap-2"
                    >
                      <Edit className="w-4 h-4" /> Edit Profile
                    </button>
                    <button
                      onClick={() => { setViewUserModal(null); setWalletModal(viewUserModal); }}
                      className="px-4 py-2 bg-accent/20 text-accent rounded-lg hover:bg-accent/30 flex items-center gap-2"
                    >
                      <Wallet className="w-4 h-4" /> Manage Wallet
                    </button>
                    <button
                      onClick={() => { setViewUserModal(null); setPasswordModal(viewUserModal); }}
                      className="px-4 py-2 bg-orange-500/20 text-orange-500 rounded-lg hover:bg-orange-500/30 flex items-center gap-2"
                    >
                      <Key className="w-4 h-4" /> Change Password
                    </button>
                    {viewUserModal.role !== 'admin' && (
                      <button
                        onClick={() => handleLoginAsUser(viewUserModal)}
                        className="px-4 py-2 bg-purple-500/20 text-purple-500 rounded-lg hover:bg-purple-500/30 flex items-center gap-2"
                      >
                        <LogIn className="w-4 h-4" /> Login as User
                      </button>
                    )}
                  </div>

                  {/* Recent Orders */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <ShoppingBag className="w-5 h-5" /> Recent Orders ({viewUserData.orders?.length || 0})
                    </h3>
                    {viewUserData.orders?.length > 0 ? (
                      <div className="bg-white/5 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-white/5">
                            <tr>
                              <th className="text-left p-3">Order ID</th>
                              <th className="text-left p-3">Amount</th>
                              <th className="text-left p-3">Status</th>
                              <th className="text-left p-3">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewUserData.orders.slice(0, 5).map((order, idx) => (
                              <tr key={idx} className="border-t border-white/5">
                                <td className="p-3 font-mono text-xs">{order.id?.slice(0, 8)}...</td>
                                <td className="p-3">₹{order.total_amount?.toFixed(2)}</td>
                                <td className="p-3">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    order.payment_status === 'completed' ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                                  }`}>
                                    {order.payment_status}
                                  </span>
                                </td>
                                <td className="p-3 text-muted-foreground">{new Date(order.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted-foreground bg-white/5 p-4 rounded-xl">No orders yet</p>
                    )}
                  </div>

                  {/* Recent Transactions */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <ArrowRightLeft className="w-5 h-5" /> Recent Transactions ({viewUserData.transactions?.length || 0})
                    </h3>
                    {viewUserData.transactions?.length > 0 ? (
                      <div className="bg-white/5 rounded-xl overflow-hidden">
                        <table className="w-full text-sm">
                          <thead className="bg-white/5">
                            <tr>
                              <th className="text-left p-3">Type</th>
                              <th className="text-left p-3">Amount</th>
                              <th className="text-left p-3">With</th>
                              <th className="text-left p-3">Date</th>
                            </tr>
                          </thead>
                          <tbody>
                            {viewUserData.transactions.slice(0, 5).map((txn, idx) => (
                              <tr key={idx} className="border-t border-white/5">
                                <td className="p-3">
                                  <span className={`px-2 py-1 rounded text-xs ${
                                    txn.sender_id === viewUserModal.id ? 'bg-red-500/20 text-red-500' : 'bg-green-500/20 text-green-500'
                                  }`}>
                                    {txn.sender_id === viewUserModal.id ? 'Sent' : 'Received'}
                                  </span>
                                </td>
                                <td className="p-3">₹{txn.amount?.toFixed(2)}</td>
                                <td className="p-3">@{txn.sender_id === viewUserModal.id ? txn.recipient_username : txn.sender_username}</td>
                                <td className="p-3 text-muted-foreground">{new Date(txn.created_at).toLocaleDateString()}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <p className="text-muted-foreground bg-white/5 p-4 rounded-xl">No transactions yet</p>
                    )}
                  </div>

                  {/* Referrals */}
                  <div>
                    <h3 className="text-lg font-semibold mb-3 flex items-center gap-2">
                      <Users className="w-5 h-5" /> Direct Referrals ({viewUserData.referrals?.length || 0})
                    </h3>
                    {viewUserData.referrals?.length > 0 ? (
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        {viewUserData.referrals.slice(0, 6).map((ref, idx) => (
                          <div key={idx} className="bg-white/5 p-3 rounded-xl">
                            <p className="font-semibold">{ref.name}</p>
                            <p className="text-sm text-muted-foreground">@{ref.username}</p>
                            <span className={`inline-block mt-1 px-2 py-0.5 rounded text-xs ${
                              ref.is_activated ? 'bg-green-500/20 text-green-500' : 'bg-yellow-500/20 text-yellow-500'
                            }`}>
                              {ref.is_activated ? 'Active' : 'Free'}
                            </span>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-muted-foreground bg-white/5 p-4 rounded-xl">No referrals yet</p>
                    )}
                  </div>
                </div>
              ) : null}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminUsers;
